import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { LOGO_URL } from "../lib/site";
import { useT } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { LanguageToggle } from "./LanguageToggle";

const Wordmark = () => (
  <Link to="/" className="flex items-center" data-testid="logo-home-link" aria-label="Common Ground Project home">
    <img src={LOGO_URL} alt="Common Ground Project" className="h-16 md:h-20 w-auto" />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled ? "bg-bone/80 backdrop-blur-xl border-b border-border" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto max-w-7xl px-5 md:px-8 h-24 flex items-center justify-between">
        <Wordmark />
        <div className="hidden lg:flex items-center gap-7">
          {t.nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to}`}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors duration-200 hover:text-terracotta ${
                  isActive ? "text-terracotta" : "text-ink/80"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/sostienici"
            data-testid="nav-donate-cta"
            className="rounded-full bg-terracotta px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-terracotta-dark"
          >
            {t.donate}
          </Link>
          {user && (
            <Link to="/area-soci" data-testid="nav-user-avatar" title={user.name || user.email}>
              <span className="h-9 w-9 rounded-full bg-forest text-bone flex items-center justify-center text-xs font-bold">
                {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
              </span>
            </Link>
          )}
          <LanguageToggle />
        </div>
        <button
          className="lg:hidden text-ink"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-bone/95 backdrop-blur-xl border-b border-border"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {t.nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `py-3 text-lg font-display font-bold tracking-tight border-b border-border/60 ${
                      isActive ? "text-terracotta" : "text-ink"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/sostienici"
                className="mt-4 rounded-full bg-terracotta px-6 py-3 text-center font-semibold text-white"
                data-testid="mobile-donate-cta"
              >
                {t.donate}
              </Link>
              <div className="mt-4 flex justify-center">
                <LanguageToggle variant="mobile" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
